import type { LearningStatus, LearningTableMeasurement } from '../shared/desktop-api';
export const learningFieldTargetKey = (row: { sessionId: string; evidenceKey: string }) =>
  `field:${row.sessionId}:${row.evidenceKey}`;
export const learningTableTargetKey = (row: {
  sessionId: string;
  evidenceKey: string;
  layoutId: string;
}) => `table:${row.sessionId}:${row.evidenceKey}:${row.layoutId}`;
export function learningTableTrialLabel(row: Pick<LearningTableMeasurement, 'checkStage'>): string {
  return row.checkStage === 'training' ||
    row.checkStage === 'search' ||
    row.checkStage === 'final-repair'
    ? 'Numeric corrections'
    : 'Table reading';
}
export type ScoredAttempt = {
  id: string;
  round: number;
  percent: number;
  right: number;
  total: number;
  declined: number | null;
  verdict: string;
  winner: boolean;
  comparisonLabel?: string;
};
export function learningResultUnavailable(
  targetKey: string | undefined,
  status: boolean,
  fields: boolean,
  tables: boolean,
) {
  return targetKey?.startsWith('field:')
    ? fields
    : targetKey?.startsWith('table:')
      ? tables
      : status;
}
export function learningReplayIsCurrent(
  live: boolean,
  unavailable: boolean,
  missing: boolean,
  historical: boolean,
  target?: { targetKey: string; historical?: boolean },
) {
  // Field/table feeds include prior sessions and carry no link to the current
  // status identity. An active job is not evidence that its saved trial is live.
  return (
    live &&
    !unavailable &&
    !missing &&
    !historical &&
    (!target || (target.targetKey.startsWith('comparison:') && target.historical === false))
  );
}
export function savedLearningComparisons(
  current: LearningStatus | null,
  history: readonly LearningStatus[],
) {
  if (!current) return [];
  const seen = new Set<string>();
  return [current, ...history].flatMap((status) => {
    if (status.jobId !== current.jobId || seen.has(status.startedAt)) return [];
    seen.add(status.startedAt);
    return (status.comparisons ?? []).map((comparison) => ({
      ...comparison,
      targetKey: `comparison:${status.startedAt}:${comparison.id}`,
      historical: status.startedAt !== current.startedAt,
    }));
  });
}
export function historicalTypeComparisons(
  current: LearningStatus | null,
  history: readonly LearningStatus[],
) {
  const seen = new Set<string>();
  return (current ? [current, ...history] : []).flatMap((status) => {
    if (!current || status.jobId !== current.jobId || seen.has(status.startedAt)) return [];
    seen.add(status.startedAt);
    return [
      ...(status.startedAt !== current.startedAt
        ? [
            {
              key: `history-types:${status.startedAt}`,
              status,
              adopted: false,
              recordedAt: status.updatedAt ?? status.startedAt,
            },
            ...(status.lastAppliedTypeComparison?.length
              ? [
                  {
                    key: `history-adopted:${status.startedAt}`,
                    status: { ...status, recent: status.lastAppliedTypeComparison, best: null },
                    adopted: true,
                    recordedAt: status.updatedAt ?? status.startedAt,
                  },
                ]
              : []),
          ]
        : []),
      ...(status.retiredTypeComparisons ?? []).map((comparison) => ({
        key: `history-comparison:${status.startedAt}:${comparison.id}`,
        status: { ...status, recent: comparison.attempts, best: null },
        adopted: comparison.attempts.some((attempt) => attempt.adopted === true),
        recordedAt: comparison.recordedAt,
      })),
    ].filter((entry) => scoredLearningAttempts(entry.status).length > 0);
  });
}
type MeasuredAttempt = {
  attempt: number;
  right: number;
  total: number;
  regressions: number;
  adopted: boolean;
  scoreScope: string;
  beforeRight?: number | null;
};
export function unmeasuredLearningReason(outcome?: string): string {
  // Closed copy only: unknown native outcomes may contain diagnostic details.
  switch (outcome) {
    case 'operator-guidance':
      return 'Your saved reading instructions were preserved. This check did not replace them.';
    case 'operator-refused':
      return 'You turned down a change to this reading earlier, so no new approach was tested.';
    case 'no-candidates':
      return 'No new approach was proposed for this value. The existing reading is unchanged.';
    case 'validation-already-reserved':
      return 'More confirmed documents are needed for a fresh independent check.';
    case 'insufficient-independent-originals':
      return 'More distinct confirmed documents are needed. Repeated imports are not new examples.';
    case 'evidence-changed':
      return 'The setup or confirmations changed before this comparison could finish.';
    case 'interrupted':
      return 'Processing interrupted the final check. Its final documents were used; a fresh check needs new confirmed documents.';
    default:
      return 'This check did not retain any measured candidate attempts.';
  }
}
export function measuredLearningAttempts(
  key: string,
  attempts: readonly MeasuredAttempt[],
): ScoredAttempt[] {
  const first = attempts[0];
  if (!first) return [];
  const seen = new Set<number>();
  for (const attempt of attempts) {
    if (
      ![attempt.attempt, attempt.right, attempt.total, attempt.regressions].every(
        Number.isSafeInteger,
      ) ||
      attempt.attempt < 1 ||
      attempt.total < 1 ||
      attempt.right < 0 ||
      attempt.right > attempt.total ||
      attempt.regressions < 0 ||
      attempt.regressions > attempt.total - attempt.right ||
      typeof attempt.adopted !== 'boolean' ||
      !attempt.scoreScope?.trim() ||
      attempt.total !== first.total ||
      attempt.scoreScope !== first.scoreScope ||
      seen.has(attempt.attempt)
    )
      return [];
    if (
      attempt.beforeRight != null &&
      (!Number.isSafeInteger(attempt.beforeRight) ||
        attempt.beforeRight < 0 ||
        attempt.beforeRight > attempt.total ||
        attempt.regressions > attempt.beforeRight)
    )
      return [];
    seen.add(attempt.attempt);
  }
  // Population identities are checked by the native projection. Aggregate checks
  // here cannot establish identity, but can refuse incomplete/inconsistent data.
  return attempts.map((attempt) => ({
    id: key + ':' + attempt.attempt,
    round: attempt.attempt,
    percent: (100 * attempt.right) / attempt.total,
    right: attempt.right,
    total: attempt.total,
    declined: null,
    winner: attempt.adopted,
    ...measuredAttemptOutcome(attempt),
  }));
}
export function learningTargetSelection<
  T extends { targetKey: string; attempts?: readonly unknown[] | null },
>(targets: readonly T[], selected: string) {
  const chosen =
    targets.find((target) => target.targetKey === selected) ??
    (!selected ? targets.find((target) => target.attempts?.length) : undefined);
  const missing = Boolean(selected && selected !== 'types' && selected !== 'types-kept' && !chosen);
  return {
    chosen,
    missing,
    value:
      chosen?.targetKey ?? (missing ? selected : selected === 'types-kept' ? selected : 'types'),
  };
}
export function measuredAttemptOutcome(attempt: {
  right: number;
  total: number;
  beforeRight?: number | null;
  regressions: number;
  adopted: boolean;
}) {
  if (attempt.adopted) return { verdict: 'Applied' };
  if (attempt.regressions > 0)
    return {
      verdict: 'Not kept: would worsen existing answers',
      comparisonLabel: 'Would lose accuracy',
    };
  const before = attempt.beforeRight;
  if (before != null && Number.isInteger(before) && before >= 0 && before <= attempt.total) {
    if (before === attempt.right)
      return {
        verdict: 'Matched the existing approach on these examples; not adopted',
        comparisonLabel: 'No accuracy gain',
      };
    if (attempt.right > before)
      return {
        verdict: 'Read more examples correctly in this comparison; not adopted',
        comparisonLabel: 'Improved in this comparison',
      };
  }
  return { verdict: 'Not selected' };
}
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function scoredLearningAttempts(status: LearningStatus | null): ScoredAttempt[] {
  if (!status) return [];
  // Retired snapshots may contain the recent sixty plus one retained best trial.
  const candidates = (status.recent ?? [])
    .filter((row) => row.lever === 'document type')
    .slice(0, 61)
    .reverse();
  // The native loop retains its best candidate independently of the recent-60
  // window and clears both on an evidence change. Keep that actual measured
  // attempt visible, without interpreting "best" as a recorded adoption.
  // Callers substituting a retired/adopted comparison must clear best: that
  // candidate belongs to the current evidence, not the substituted population.
  if (
    status.best?.lever === 'document type' &&
    !candidates.some((row) => row.round === status.best!.round)
  ) {
    candidates.push(status.best);
    candidates.sort((left, right) => left.round - right.round);
  }
  const rounds = new Set<number>();
  for (const row of candidates) {
    if (
      !row.mining ||
      !Number.isSafeInteger(row.round) ||
      row.round < 1 ||
      rounds.has(row.round) ||
      ![row.mining.right, row.mining.wrong, row.mining.declined].every(
        (value) => Number.isSafeInteger(value) && value >= 0,
      ) ||
      (row.adopted != null && typeof row.adopted !== 'boolean')
    )
      return [];
    rounds.add(row.round);
  }
  const totals = candidates.map((row) => row.mining.right + row.mining.wrong + row.mining.declined);
  if (totals.some((total) => !Number.isSafeInteger(total) || total < 1)) return [];
  if (new Set(totals).size > 1) return [];
  return candidates.flatMap((row) => {
    const { right, wrong, declined } = row.mining,
      total = right + wrong + declined;
    if (
      ![right, wrong, declined].every((value) => Number.isFinite(value) && value >= 0) ||
      total === 0
    )
      return [];
    return [
      {
        id: `${status.startedAt}:${row.round}:${row.summary}`,
        round: row.round,
        percent: (100 * right) / total,
        right,
        total,
        declined,
        ...(right === 0
          ? {
              comparisonLabel:
                declined === total
                  ? 'All examples left unanswered'
                  : declined === 0
                    ? 'All answers incorrect'
                    : `${wrong} incorrect · ${declined} unanswered`,
            }
          : {}),
        verdict: row.adopted
          ? 'Applied'
          : row.adoptionHoldReason
            ? row.adoptionHoldReason
            : row.regressedSomething
              ? 'Not kept: would worsen existing answers'
              : row.verdict === 'promotable'
                ? 'Passed checks; adoption not recorded here'
                : 'Not selected',
        winner: row.adopted === true,
      },
    ];
  });
}
export const exampleLearningAttempts: ScoredAttempt[] = [3, 17, 92, 80, 62].map(
  (percent, index) => ({
    id: 'example:' + index,
    round: index + 1,
    percent,
    right: percent,
    total: 100,
    declined: 0,
    verdict:
      index === 2 ? 'Best verified approach in this example' : 'Not selected in this example',
    winner: index === 2,
  }),
);
export function learningAttemptPage<T extends { winner: boolean }>(
  rows: readonly T[],
  requested: number | null,
) {
  const size = 8;
  const count = Math.max(1, Math.ceil(rows.length / size));
  const winner = rows.findIndex((row) => row.winner);
  const preferred = winner < 0 ? count - 1 : Math.floor(winner / size);
  const page = Math.min(count - 1, Math.max(0, requested ?? preferred));
  return { page, count, start: page * size, rows: rows.slice(page * size, (page + 1) * size) };
}
export function attemptGeometry(percent: number, index: number, count: number) {
  const y = 70 + index * 62,
    originY = (70 + (count - 1) * 62) / 2;
  const end = 60 + Math.max(0, Math.min(100, percent)) * 6.3;
  const nodes = Array.from({ length: 7 }, (_, step) => ({
    x: 60 + ((end - 60) * step) / 6,
    y: step === 6 ? y : y + Math.sin((index + 2) * 7 + step * 2.2) * 12,
  }));
  return {
    y,
    end,
    nodes,
    d:
      `M 4 ${originY} Q 30 ${originY} ${nodes[0].x} ${nodes[0].y} ` +
      nodes
        .slice(1)
        .map((node) => `L ${node.x} ${node.y}`)
        .join(' '),
  };
}
